import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  PayloadAction,
  EntityState,
} from "@reduxjs/toolkit";
import type { RootState } from "@/app/types";
import { fetchLearningPaths } from "@/api/fetchLearningPaths";
import { createLearningPath } from "@/api/createLearningPath";
import { duplicateLearningPath } from "@/api/duplicateLearningPath";
import { updateLearningPath } from "@/api/updateLearningPath";
import { updateLearningPathSettings } from "@/api/updateLearningPathSettings";
import type {
  LearningPath,
  CreatePathRequest,
  UpdatePathRequest,
  ListPathsParams,
  LearningPathResponse,
} from "@/modules/learningCenter/features/LearningPaths/apiTypes";

/**
 * Learning Paths Slice
 * Manages CRUD operations for learning paths
 * Part of Feature 010-1: Learning Paths
 */

// Entity adapter for normalized state management
const learningPathsAdapter = createEntityAdapter<LearningPath>({
  sortComparer: (a, b) => {
    // Sort by updatedAt descending (most recently updated first)
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  },
});

// State interface
interface LearningPathsState {
  paths: EntityState<LearningPath, number>;
  selectedPathId: number | null;
  selectedPathDetail: LearningPathResponse | null;
  loading: boolean;
  error: string | null;
  detailLoading: boolean;
  detailError: string | null;
  createLoading: boolean;
  createError: string | null;
  updateLoading: boolean;
  updateError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  filters: {
    searchQuery: string;
    companyId?: number;
    createdBy?: number;
    isArchived: boolean;
    /** Audience filters (admin catalog); "" = any. */
    stage: string;
    trade: string;
    role: string;
  };
  total: number;
}

// Initial state
const initialState: LearningPathsState = {
  paths: learningPathsAdapter.getInitialState(),
  selectedPathId: null,
  selectedPathDetail: null,
  loading: false,
  error: null,
  detailLoading: false,
  detailError: null,
  createLoading: false,
  createError: null,
  updateLoading: false,
  updateError: null,
  deleteLoading: false,
  deleteError: null,
  filters: {
    searchQuery: "",
    companyId: undefined,
    createdBy: undefined,
    isArchived: false,
    stage: "",
    trade: "",
    role: "",
  },
  total: 0,
};

// Async Thunks

/**
 * Fetch learning paths with filters
 */
export const fetchPaths = createAsyncThunk(
  "learningPaths/fetchPaths",
  async (params: ListPathsParams | undefined, { rejectWithValue }) => {
    try {
      const response = await fetchLearningPaths(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch learning paths",
      );
    }
  },
);

/**
 * Fetch a single learning path with full details (courses and milestones)
 */
export const fetchPathDetail = createAsyncThunk(
  "learningPaths/fetchPathDetail",
  async (pathId: number, { rejectWithValue }) => {
    try {
      // Note: This endpoint would need to be created in the API layer
      // For now, we'll use the update endpoint pattern but as a GET
      // You may need to create a dedicated fetchPathDetail API function
      const response = await fetchLearningPaths({
        /* filter by ID if supported */
      });
      // This is a placeholder - in production you'd have a dedicated endpoint
      // like: const response = await fetchLearningPathDetail(pathId);
      return response as unknown as LearningPathResponse;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch path details",
      );
    }
  },
);

/**
 * Create a new learning path
 */
export const createPath = createAsyncThunk(
  "learningPaths/createPath",
  async (payload: CreatePathRequest, { rejectWithValue }) => {
    try {
      const response = await createLearningPath(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to create learning path",
      );
    }
  },
);

/**
 * Update an existing learning path
 */
export const updatePath = createAsyncThunk(
  "learningPaths/updatePath",
  async (
    { id, payload }: { id: number; payload: UpdatePathRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateLearningPath(id, payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update learning path",
      );
    }
  },
);

/**
 * Delete (archive) a learning path
 * Note: This implements soft delete by setting isArchived to true
 */
export const deletePath = createAsyncThunk(
  "learningPaths/deletePath",
  async (id: number, { rejectWithValue }) => {
    try {
      // Archive the path instead of hard delete
      const response = await updateLearningPath(id, { isArchived: true });
      return { id, path: response.path };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to delete learning path",
      );
    }
  },
);

/**
 * Update path settings (self-enrollment, archive status)
 * Uses dedicated settings endpoint: PUT /api/learning-paths/{id}/settings
 */
export const updatePathSettings = createAsyncThunk(
  "learningPaths/updatePathSettings",
  async (
    {
      id,
      selfEnrollmentEnabled,
      isArchived,
    }: {
      id: number;
      selfEnrollmentEnabled?: boolean;
      isArchived?: boolean;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateLearningPathSettings(id, {
        selfEnrollmentEnabled,
        isArchived,
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to update path settings",
      );
    }
  },
);

/**
 * Duplicate a learning path (M4): POST /api/learning-paths/{id}/duplicate.
 * Deep copy → always a NEW DRAFT owned by the caller (never enrollments).
 * Resolves the new path's canonical GraphPath envelope so the caller can
 * navigate straight to its flow-builder editor; the list state is left
 * untouched (the caller navigates away, and drafts surface on refetch).
 */
export const duplicatePath = createAsyncThunk(
  "learningPaths/duplicatePath",
  async (
    { pathId, name }: { pathId: number; name?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await duplicateLearningPath(
        pathId,
        name !== undefined ? { name } : {},
      );
      return response.data.path;
    } catch (error) {
      const failure = error as {
        response?: { data?: { error?: unknown; message?: unknown } };
      };
      const apiMessage = failure.response?.data?.error;
      const fallbackMessage = failure.response?.data?.message;
      return rejectWithValue(
        typeof apiMessage === "string" && apiMessage !== ""
          ? apiMessage
          : typeof fallbackMessage === "string" && fallbackMessage !== ""
            ? fallbackMessage
            : "Failed to duplicate learning path",
      );
    }
  },
);

// Slice
const learningPathsSlice = createSlice({
  name: "learningPaths",
  initialState,
  reducers: {
    setSelectedPathId: (state, action: PayloadAction<number | null>) => {
      state.selectedPathId = action.payload;
    },
    clearSelectedPath: (state) => {
      state.selectedPathId = null;
      state.selectedPathDetail = null;
    },
    setFilters: (
      state,
      action: PayloadAction<Partial<LearningPathsState["filters"]>>,
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearErrors: (state) => {
      state.error = null;
      state.detailError = null;
      state.createError = null;
      state.updateError = null;
      state.deleteError = null;
    },
    clearPaths: (state) => {
      state.paths = learningPathsAdapter.getInitialState();
      state.total = 0;
    },
  },
  extraReducers: (builder) => {
    // Fetch paths
    builder
      .addCase(fetchPaths.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaths.fulfilled, (state, action) => {
        state.loading = false;
        // Handle case where API returns undefined/null for paths
        const paths = action.payload?.paths || [];
        learningPathsAdapter.setAll(state.paths, paths);
        state.total = action.payload?.total || 0;
      })
      .addCase(fetchPaths.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch path detail
    builder
      .addCase(fetchPathDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchPathDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedPathDetail = action.payload;
        // Also update the entity in the normalized state
        learningPathsAdapter.upsertOne(state.paths, action.payload.path);
      })
      .addCase(fetchPathDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload as string;
      });

    // Create path
    builder
      .addCase(createPath.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createPath.fulfilled, (state, action) => {
        state.createLoading = false;
        learningPathsAdapter.addOne(state.paths, action.payload.path);
        state.total += 1;
        // Set as selected
        state.selectedPathId = action.payload.path.id;
        state.selectedPathDetail = action.payload;
      })
      .addCase(createPath.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload as string;
      });

    // Update path
    builder
      .addCase(updatePath.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updatePath.fulfilled, (state, action) => {
        state.updateLoading = false;
        learningPathsAdapter.upsertOne(state.paths, action.payload.path);
        // Update selected detail if it's the same path. The PUT echoes only
        // the path itself, so keep the detail's courses and milestones.
        if (
          state.selectedPathId === action.payload.path.id &&
          state.selectedPathDetail
        ) {
          state.selectedPathDetail.path = action.payload.path;
        }
      })
      .addCase(updatePath.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });

    // Delete path (archive)
    builder
      .addCase(deletePath.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deletePath.fulfilled, (state, action) => {
        state.deleteLoading = false;
        // Update the path with archived status
        learningPathsAdapter.upsertOne(state.paths, action.payload.path);
        // If filters exclude archived, remove from list
        if (!state.filters.isArchived) {
          learningPathsAdapter.removeOne(state.paths, action.payload.id);
          state.total -= 1;
        }
        // Clear selection if deleted
        if (state.selectedPathId === action.payload.id) {
          state.selectedPathId = null;
          state.selectedPathDetail = null;
        }
      })
      .addCase(deletePath.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      });

    // Update path settings
    builder
      .addCase(updatePathSettings.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updatePathSettings.fulfilled, (state, action) => {
        state.updateLoading = false;
        const path = action.payload;
        learningPathsAdapter.upsertOne(state.paths, path);
        // Archiving (or restoring) can push the path out of the active filter —
        // drop it from the list rather than leaving a stale row behind.
        if (path.isArchived !== state.filters.isArchived) {
          learningPathsAdapter.removeOne(state.paths, path.id);
          state.total = Math.max(0, state.total - 1);
        }
        // Update selected detail if it's the same path
        if (state.selectedPathId === path.id && state.selectedPathDetail) {
          state.selectedPathDetail = { ...state.selectedPathDetail, path };
        }
      })
      .addCase(updatePathSettings.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.payload as string;
      });
  },
});

// Actions
export const {
  setSelectedPathId,
  clearSelectedPath,
  setFilters,
  resetFilters,
  clearErrors,
  clearPaths,
} = learningPathsSlice.actions;

// Selectors using entity adapter
export const {
  selectAll: selectAllPathsEntities,
  selectById: selectPathById,
  selectIds: selectPathIds,
  selectEntities: selectPathEntities,
  selectTotal: selectPathsCount,
} = learningPathsAdapter.getSelectors(
  (state: RootState) => state.learningPaths.paths,
);

// Custom selectors
export const selectAllPaths = (state: RootState) => {
  const paths = selectAllPathsEntities(state);
  const filters = state.learningPaths.filters;

  // Apply filters
  return paths.filter((path) => {
    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const matchesName = path.name.toLowerCase().includes(query);
      const matchesDescription = path.description
        ?.toLowerCase()
        .includes(query);
      if (!matchesName && !matchesDescription) {
        return false;
      }
    }

    // Filter by company
    if (
      filters.companyId !== undefined &&
      path.companyId !== filters.companyId
    ) {
      return false;
    }

    // Filter by creator
    if (
      filters.createdBy !== undefined &&
      path.createdBy !== filters.createdBy
    ) {
      return false;
    }

    // Filter by archive status
    if (path.isArchived !== filters.isArchived) {
      return false;
    }

    // Audience: a stage must match; a trade / job title matches when the
    // path names it or names none (no tags = for everyone).
    if (filters.stage && !(path.growthStages ?? []).includes(filters.stage)) {
      return false;
    }
    if (
      filters.trade &&
      (path.trades?.length ?? 0) > 0 &&
      !path.trades?.includes(filters.trade)
    ) {
      return false;
    }
    if (
      filters.role &&
      (path.roles?.length ?? 0) > 0 &&
      !path.roles?.includes(filters.role)
    ) {
      return false;
    }

    return true;
  });
};

export const selectActivePaths = (state: RootState) => {
  return selectAllPathsEntities(state).filter((path) => !path.isArchived);
};

export const selectMyCreatedPaths = (userId: number) => (state: RootState) => {
  return selectAllPathsEntities(state).filter(
    (path) => path.createdBy === userId && !path.isArchived,
  );
};

export const selectSelectedPathId = (state: RootState) =>
  state.learningPaths.selectedPathId;

export const selectSelectedPathDetail = (state: RootState) =>
  state.learningPaths.selectedPathDetail;

export const selectSelectedPath = (state: RootState) => {
  const selectedId = state.learningPaths.selectedPathId;
  if (!selectedId) return null;
  return selectPathById(state, selectedId);
};

export const selectPathsLoading = (state: RootState) =>
  state.learningPaths.loading;

export const selectPathsError = (state: RootState) => state.learningPaths.error;

export const selectDetailLoading = (state: RootState) =>
  state.learningPaths.detailLoading;

export const selectDetailError = (state: RootState) =>
  state.learningPaths.detailError;

export const selectCreateLoading = (state: RootState) =>
  state.learningPaths.createLoading;

export const selectCreateError = (state: RootState) =>
  state.learningPaths.createError;

export const selectUpdateLoading = (state: RootState) =>
  state.learningPaths.updateLoading;

export const selectUpdateError = (state: RootState) =>
  state.learningPaths.updateError;

export const selectDeleteLoading = (state: RootState) =>
  state.learningPaths.deleteLoading;

export const selectDeleteError = (state: RootState) =>
  state.learningPaths.deleteError;

export const selectPathsFilters = (state: RootState) =>
  state.learningPaths.filters;

export const selectPathsTotal = (state: RootState) => state.learningPaths.total;

export const selectPathsByCompany =
  (companyId: number) => (state: RootState) => {
    return selectAllPathsEntities(state).filter(
      (path) => path.companyId === companyId && !path.isArchived,
    );
  };

// Reducer
export default learningPathsSlice.reducer;
